import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CounselorNav } from '../../counselor-nav/counselor-nav';
import { UserAvatar } from '../../../../common/user-avatar/user-avatar';

import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import { USER_PERMISSIONS } from '../../../../../constants/permissions';

import {
  StudentService,
  CounselorStudent,
  GRADE_LABELS,
} from '../../../../../services/student.service';
import { GroupService } from '../../../../../services/group.service';
import { Group } from '../../../../../models/group';
import { GroupDialog, GroupDialogData } from '../group-dialog/group-dialog';
import { User } from '../../../../../models/user';

@Component({
  selector: 'app-group-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatListModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    CounselorNav,
    UserAvatar,
    HasPermissionDirective,
  ],
  templateUrl: './group-page.html',
  styleUrl: './group-page.css',
})
export class GroupPage implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private groupService = inject(GroupService);
  private studentService = inject(StudentService);
  private dialog = inject(MatDialog);

  permissions = USER_PERMISSIONS;

  loading = signal(true);
  error = signal<string | null>(null);
  group = signal<Group | null>(null);
  allStudents = signal<CounselorStudent[]>([]);
  pendingAddStudentId: number | null = null;

  private studentByUserId = computed(() => {
    const map = new Map<number, CounselorStudent>();
    this.allStudents().forEach((s) => map.set(s.user.id, s));
    return map;
  });

  members = computed<CounselorStudent[]>(() => {
    const g = this.group();
    if (!g) return [];
    const map = this.studentByUserId();
    return g.members.map((u) => map.get(u.id)).filter((s): s is CounselorStudent => !!s);
  });

  availableStudents = computed<CounselorStudent[]>(() => {
    const g = this.group();
    if (!g) return [];
    const memberUserIds = new Set(g.members.map((u) => u.id));
    return this.allStudents().filter((s) => !memberUserIds.has(s.user.id));
  });

  ngOnInit(): void {
    const groupId = Number(this.route.snapshot.paramMap.get('groupId'));

    if (!groupId) {
      this.error.set('Invalid group.');
      this.loading.set(false);
      return;
    }

    this.studentService.getMyStudents().subscribe({
      next: (students) => this.allStudents.set(students),
      error: (err) => console.error('Failed to load students for group page:', err),
    });

    this.loadGroup(groupId);
  }

  loadGroup(groupId: number) {
    this.groupService.getManagedGroups().subscribe({
      next: (groups) => {
        const found = groups.find((g) => g.id === groupId) ?? null;
        if (!found) {
          this.error.set('Group not found.');
        } else {
          this.group.set(found);
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error(err);
        this.error.set('Failed to load group. Please try again.');
        this.loading.set(false);
      },
    });
  }

  canManage(): boolean {
    return this.group()?.source === 'manual';
  }

  sourceLabel(): string {
    return this.group()?.source === 'oneroster' ? 'Synced from OneRoster' : 'Custom group';
  }

  gradeLabel(level: number): string {
    return GRADE_LABELS[level] ?? `Grade ${level}`;
  }

  goBack(): void {
    this.router.navigate(['/counselors/students'], { queryParams: { view: 'groups' } });
  }

  openEditGroup(): void {
    const g = this.group();
    const groupId = this.group()!.id;
    if (!g) return;

    const ref = this.dialog.open<GroupDialog, GroupDialogData>(GroupDialog, {
      width: '520px',
      maxWidth: '95vw',
      data: { group: g, students: this.allStudents() },
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.updated) {
        this.loadGroup(groupId);
      }
    });
  }
  addMember(): void {
    const g = this.group();
    const studentUserId = this.pendingAddStudentId;
    if (!g || !studentUserId) return;

    const student = this.studentByUserId().get(studentUserId);
    if (!student) return;

    this.groupService.addGroupMember(g.id, studentUserId).subscribe({
      next: () => {
        this.group.update((current) => {
          if (!current) return current;
          if (current.members.some((u) => u.id === studentUserId)) return current;
          return { ...current, members: [...current.members, User.digest(student.user)] };
        });
        this.pendingAddStudentId = null;
      },
      error: (err) => console.error('addGroupMember failed:', err),
    });
  }
  removeMember(studentUserId: number): void {
    const g = this.group();
    if (!g) return;

    this.groupService.removeGroupMember(g.id, studentUserId).subscribe({
      next: () => {
        this.group.update((current) => {
          if (!current) return current;
          return {
            ...current,
            members: current.members.filter((u) => u.id !== studentUserId),
          };
        });
      },
      error: (err) => console.error('removeGroupMember failed:', err),
    });
  }
}
